import { useEffect, useMemo } from 'react';
import Iconify from 'src/components/Iconify';
import { Autocomplete, Dialog, Grid, IconButton, Stack, TextField } from '@mui/material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { TaskInput } from 'src/careflow/ph-core/fhir-client/resources';
import { ErrorFieldState } from './TaskModal';
import { GridSection } from 'src/components/CustomModal';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useObjectState from 'src/hooks/useObjectState';
import { FileUpload } from '@mui/icons-material';
import {
  canViewDocument,
  SelectAttachmentState,
  VALUE_KEYS,
  ValueKey,
} from 'src/careflow/common/document/document';
import { TaskDocument } from './TaskDocument';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { fhirClient } from 'src/App';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { WrappedDocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';

type Props = {
  task: WrappedTask | undefined;
  taskInput: TaskInput[] | null;
  patient: WrappedPatient | null;
  updateState: Function;
  errors: ErrorFieldState;
  updateErrorState: Function;
  i18n: i18nFunction;
  configurations: any;
};

const TaskInputComponent = ({
  task,
  taskInput,
  patient,
  errors,
  updateState,
  updateErrorState,
  i18n,
  configurations,
}: Props) => {
  const [{ openUploadFile, index, selectedDocumentReference }, updateSelectAttachmentState] =
    useObjectState<SelectAttachmentState>({
      openUploadFile: false,
      index: null,
      selectedDocumentReference: null,
    });

  useEffect(() => {
    if (taskInput === undefined) {
      updateState({
        taskInputs: [{ type: {}, valueString: '' }],
      });
    }
  }, [taskInput]);

  const {
    valueSets: [taskInputTypeVS],
    isLoading: isFetchingVS,
  } = useValueSetsByIdentifiers(['ph-task-input-types']);
  const { fhir, inputTypeFiles } = configurations ?? {};

  const handleAddInput = () => {
    updateState({
      taskInputs: [...(taskInput ?? []), { type: {}, valueString: '' }],
    });
  };

  const inputTypeCodeableConcept = useMemo(
    () =>
      (taskInputTypeVS?.asList?.() ?? []).map((concept) => ({
        coding: [
          {
            code: concept?.code,
            display: concept?.display,
            system: taskInputTypeVS?.getConceptSystem?.(),
          },
        ],
      })),
    [taskInputTypeVS]
  );

  const displayInputs =
    taskInput && Array.isArray(taskInput) && taskInput.length > 0
      ? taskInput
      : [{ type: {}, valueString: '' }];

  const getCurrentInputs = () =>
    taskInput && Array.isArray(taskInput) && taskInput.length > 0
      ? taskInput.map((item) => ({
          ...(item ?? {}),
          type: item?.type ? { ...item.type } : item?.type,
        }))
      : [{ type: {}, valueString: '' }];

  const canEditInput = checkAclValidation({
    acls: [crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.EDIT, crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.EDIT],
  });

  const canDeleteInput = checkAclValidation({
    acls: [
      crsAcls.CRS.PATIENT.TASKS.INPUT_OUTPUT.DELETE,
      crsAcls.CRS.CASE.TASKS.INPUT_OUTPUT.DELETE,
    ],
  });

  const handleTaskInputChange = (index: number, field: 'type' | 'value', value: any) => {
    if (!canEditInput) return;

    const updatedInputs = getCurrentInputs();

    if (!updatedInputs[index]) {
      updatedInputs[index] = { type: {} };
    }

    if (field === 'type') {
      const inputType = value?.coding?.[0]?.code;
      const currentInput = { ...updatedInputs[index], type: value };

      if (inputType) {
        VALUE_KEYS.forEach((key) => {
          delete currentInput[key];
        });
      }

      updatedInputs[index] = currentInput;

      if (value) {
        updateErrorState({ taskInputs: null });
      }
    }

    if (field === 'value') {
      const inputType = updatedInputs[index]?.type?.coding?.[0]?.code;

      if (!inputType) {
        updatedInputs[index] = {
          ...updatedInputs[index],
          valueString: value,
        };
        updateState({ taskInputs: updatedInputs });
        return;
      }

      const config = inputTypeFiles[inputType];
      const valueKey: ValueKey = config?.valueType ?? 'valueString';
      const currentInput = { ...updatedInputs[index] };

      VALUE_KEYS.forEach((key) => {
        delete currentInput[key];
      });

      updatedInputs[index] = {
        ...currentInput,
        [valueKey]: value,
      };
    }

    updateState({ taskInputs: updatedInputs });
  };

  const handlePreview = async (inputValue: any) => {
    updateSelectAttachmentState({ selectedDocumentReference: null });
    if (!inputValue) return;

    if (inputValue.startsWith('http')) {
      window.open(inputValue, '_blank');
      return;
    }

    if (inputValue.includes('DocumentReference')) {
      const id = inputValue.split('/')[1];

      const res = await fhirClient.findById('DocumentReference', id);
      const data = res as WrappedDocumentReference;

      if (data?.id) {
        updateSelectAttachmentState({ selectedDocumentReference: data });
      }
    }
  };

  const exists = (index, value) =>
    errors?.taskInputs?.some(
      (item) => item?.index === index && item?.invalidFields?.includes(value)
    );

  return (
    <Stack direction="column" mt={2} ml={2} mb={2}>
      {displayInputs.map((item: any, index) => {
        const inputValue =
          item?.valueString ?? item?.valueUrl ?? item?.valueReference?.reference ?? '';
        const inputType = item?.type?.coding?.[0]?.code ?? '';

        const { canPreviewDocumentReference, canPreviewUrl, canUpload, isFile } = canViewDocument(
          inputTypeFiles,
          inputType,
          inputValue
        );

        return (
          <GridSection container spacing={2} key={`input-${index}`} mt={index === 0 ? -2 : 0.4}>
            <Grid item xs={4}>
              <Autocomplete
                disabled={isFetchingVS || !canEditInput || canPreviewDocumentReference}
                value={item.type ?? undefined}
                fullWidth
                onChange={(_, newValue) => {
                  handleTaskInputChange(index, 'type', newValue);
                }}
                options={inputTypeCodeableConcept}
                getOptionLabel={(option: any) =>
                  option?.coding?.[0]?.display ?? option?.coding?.[0]?.code ?? ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n('patients.details.tasks.inputType', 'crs')}
                    variant="outlined"
                    error={exists(index, 'type')}
                    helperText={
                      exists(index, 'type') &&
                      i18n('patients.details.tasks.typeIOputRequired', 'crs')
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                disabled={isFetchingVS || !canEditInput || canPreviewDocumentReference || isFile}
                value={inputValue}
                onChange={(e) => {
                  handleTaskInputChange(index, 'value', e.target.value);
                }}
                label={i18n('patients.details.tasks.input', 'crs')}
                variant="outlined"
                error={exists(index, 'value')}
                helperText={
                  exists(index, 'value') && i18n('patients.details.tasks.valueIOputRequired', 'crs')
                }
              />
            </Grid>

            <Grid item xs={2}>
              {canUpload && (
                <IconButton
                  onClick={() =>
                    updateSelectAttachmentState({ openUploadFile: true, index: index })
                  }
                  size="medium"
                  sx={{ '&:hover': { background: 'none' } }}
                  disabled={isFetchingVS || !canEditInput}
                >
                  <FileUpload />
                </IconButton>
              )}

              {(canPreviewUrl || canPreviewDocumentReference) && (
                <IconButton
                  onClick={() => handlePreview(inputValue)}
                  size="medium"
                  sx={{ '&:hover': { background: 'none' } }}
                >
                  <Iconify icon="eva:eye-outline" />
                </IconButton>
              )}
              <IconButton
                onClick={handleAddInput}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canEditInput}
              >
                <Iconify icon="eva:plus-fill" />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  if (!canDeleteInput) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const currentInputs =
                    taskInput && Array.isArray(taskInput) && taskInput.length > 0
                      ? [...taskInput]
                      : [];
                  if (currentInputs.length === 0) return;
                  const updated = currentInputs.filter((_, i) => i !== index);
                  updateState({ taskInputs: updated.length > 0 ? updated : null });
                  if (errors?.taskInputs) {
                    const newErrors = errors.taskInputs.filter((_, i) => i !== index);
                    updateErrorState({
                      taskInputs: newErrors.length > 0 ? newErrors : null,
                    });
                  }
                }}
                size="medium"
                sx={{ '&:hover': { background: 'none' } }}
                disabled={isFetchingVS || !canDeleteInput}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>
            </Grid>
          </GridSection>
        );
      })}
      <TaskDocument
        open={openUploadFile}
        setOpen={() =>
          updateSelectAttachmentState({
            openUploadFile: false,
            index: null,
            selectedDocumentReference: null,
          })
        }
        task={task}
        patient={patient}
        configurations={fhir}
        i18n={i18n}
        updateState={(value) => {
          if (index !== null) {
            handleTaskInputChange(index, 'value', value);
          }
        }}
      />
      {selectedDocumentReference && (
        <Dialog
          fullWidth
          maxWidth="md"
          open={selectedDocumentReference !== null}
          onClose={() => updateSelectAttachmentState({ selectedDocumentReference: null })}
        >
          <Viewer
            documentReference={selectedDocumentReference}
            typeResource="documentReference"
            patientId={patient?.id}
          />
        </Dialog>
      )}
    </Stack>
  );
};

export default TaskInputComponent;
